import React from 'react';
import {FieldError} from 'react-hook-form';
import type {DropdownIndicatorProps, GroupBase} from 'react-select';
import ReactSelect, {components} from 'react-select';
import CreatableSelect from 'react-select/creatable';
import {SelectorIcon} from '@heroicons/react/outline';
import classNames from 'classnames';
import {getErrorMessage} from 'helpers/utils';
import {getSelectStyles} from './utils';

export interface OptionType {
  label: string | number;
  value: string | number | null;
}

interface SelectProps<Option, IsMulti extends boolean> extends React.ComponentProps<typeof ReactSelect> {
  label?: string;
  isCreateble?: boolean;
  error?: FieldError | FieldError[];
  isMulti?: IsMulti;
  option?: Option;
  noMarginBottom?: boolean;
}

export const Select = <Option, IsMulti extends boolean = false>(props: SelectProps<Option, IsMulti>) => {
  const {
    id,
    label,
    placeholder = 'Select...',
    noMarginBottom = false,
    isCreateble = false,
    error,
    className,
    required,
    ...selectProps
  } = props;

  const errorMessage = error ? getErrorMessage(error, label) : null;

  return (
    <div className={classNames('text-input-wrapper', className)}>
      {label && (
        <label htmlFor={id}>
          {label} {required && <span>*</span>}
        </label>
      )}
      {isCreateble ? (
        <CreatableSelect
          inputId={id}
          placeholder={placeholder}
          styles={getSelectStyles(!!errorMessage, noMarginBottom)}
          components={{
            DropdownIndicator,
            IndicatorSeparator: () => null,
          }}
          {...selectProps}
        />
      ) : (
        <ReactSelect
          inputId={id}
          placeholder={placeholder}
          styles={getSelectStyles(!!errorMessage, noMarginBottom)}
          components={{
            DropdownIndicator,
            IndicatorSeparator: () => null,
          }}
          {...selectProps}
        />
      )}
      {errorMessage && <p className="input-error-message">{errorMessage}</p>}
    </div>
  );
};

const DropdownIndicator: React.FC<DropdownIndicatorProps<unknown, boolean, GroupBase<unknown>>> = (props) => (
  <components.DropdownIndicator {...props}>
    <SelectorIcon className="h-5 w-5" aria-hidden="true" />
  </components.DropdownIndicator>
);
